import {
  isPWA,
  isIOS,
  isBlankPage,
  checkServiceWorkerUpdate,
} from "./pwaUtils";

export class ServiceWorkerManager {
  private static instance: ServiceWorkerManager;
  private isInitialized = false;

  static getInstance(): ServiceWorkerManager {
    if (!ServiceWorkerManager.instance) {
      ServiceWorkerManager.instance = new ServiceWorkerManager();
    }
    return ServiceWorkerManager.instance;
  }

  /**
   * Service Worker 초기화
   */
  initialize(): void {
    // iOS에서는 Service Worker를 초기화하지 않음
    if (isIOS()) {
      console.log("iOS detected - Service Worker initialization skipped");
      return;
    }

    if (this.isInitialized || !("serviceWorker" in navigator)) {
      return;
    }

    this.setupMessageListener();
    this.setupUpdateListener();
    this.setupVisibilityChangeListener();
    this.setupErrorHandlers();
    this.setupBlankPageCheck();

    this.isInitialized = true;
  }

  /**
   * Service Worker 메시지 리스너 설정
   */
  private setupMessageListener(): void {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data && event.data.type === "REFRESH_PAGE") {
        window.location.reload();
      }
    });
  }

  /**
   * Service Worker 업데이트 리스너 설정
   */
  private setupUpdateListener(): void {
    navigator.serviceWorker.ready.then((registration) => {
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // PWA에서는 즉시 새로고침
              if (isPWA()) {
                newWorker.postMessage({ type: "SKIP_WAITING" });
                setTimeout(() => window.location.reload(), 100);
              }
            }
          });
        }
      });
    });
  }

  /**
   * 앱 가시성 변경 리스너 설정
   */
  private setupVisibilityChangeListener(): void {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // iOS PWA에서 특별 처리
        if (isIOS() && isPWA()) {
          checkServiceWorkerUpdate();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  /**
   * 에러 핸들러 설정
   */
  private setupErrorHandlers(): void {
    const handleError = (error: ErrorEvent) => {
      console.error("App error:", error);

      // PWA에서 흰 화면 문제 감지시 강제 새로고침
      if (isPWA() && isBlankPage()) {
        console.log("Blank page detected in PWA, forcing reload...");
        window.location.reload();
        return;
      }

      // Service Worker 업데이트
      this.updateAllServiceWorkers();
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      if (isPWA() && isIOS()) {
        setTimeout(() => window.location.reload(), 500);
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
  }

  /**
   * 페이지 로드 완료 후 흰 화면 체크
   */
  private setupBlankPageCheck(): void {
    if (isIOS()) return;
    const checkBlankPageOnLoad = () => {
      setTimeout(() => {
        if (isPWA() && isIOS()) {
          const hasVisibleContent =
            document.querySelector(
              'main, [role="main"], #root > *, body > div'
            ) !== null;
          if (!hasVisibleContent) {
            console.log("No visible content detected, reloading...");
            window.location.reload();
          }
        }
      }, 1000);
    };

    window.addEventListener("load", checkBlankPageOnLoad);
  }

  /**
   * 모든 Service Worker 업데이트
   */
  private async updateAllServiceWorkers(): Promise<void> {
    if (isIOS() || !("serviceWorker" in navigator)) return;

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.update())
    );
  }

  /**
   * 정리 함수
   */
  cleanup(): void {
    // 필요한 경우 이벤트 리스너 정리
    this.isInitialized = false;
  }
}
